import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'
import { searchWords } from '@/lib/supabase/search-filter'
import type { Generation } from './generations'
import type { ModelShot } from './model-shots'
import type { Tag } from './tags'

// =============================================================================
// Model Types
// =============================================================================

type ModelRow = Database['public']['Tables']['models']['Row']

/**
 * Model with current generation and author profile.
 * Models are domain entities; generations are their versions/transformations.
 * current_generation embeds the full Generation including its author profile.
 */
export interface Model extends ModelRow {
  profiles: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
  current_generation: Generation | null
  model_tags: { tags: Tag }[]
}

/** A talent with its cast. Library and Lumoo talents have no workspace. */
export interface Talent extends ModelRow {
  model_shots: ModelShot[]
}

export interface LibraryTalent extends Talent {
  roster_entries: { count: number }[]
}

export type TalentHome = 'workspace' | 'library' | 'lumoo'

export function talentHome(talent: ModelRow): TalentHome {
  if (talent.workspace_id) {
    return 'workspace'
  }
  return talent.org_id ? 'library' : 'lumoo'
}

export type LibrarySource = 'all' | 'org' | 'lumoo'

const PAGE_SIZE = 16
const MIN_SEARCH_WORD_LENGTH = 2

export interface ModelFilters {
  authorId?: string | null
  searchQuery?: string | null
  tagIds?: string[] | null
}

/**
 * Apply model filters to any Supabase query builder.
 * Works with both select and update queries.
 */
function applyModelFilters<
  T extends {
    eq: (col: string, val: string) => T
    is: (col: string, val: null) => T
    ilike: (col: string, val: string) => T
    contains: (col: string, val: string[]) => T
  },
>(query: T, filters?: ModelFilters): T {
  let q = query

  if (filters?.authorId) {
    q = q.eq('author', filters.authorId)
  }

  // Every selected label must be present.
  if (filters?.tagIds && filters.tagIds.length > 0) {
    q = q.contains('tag_ids', filters.tagIds)
  }

  if (filters?.searchQuery) {
    for (const word of searchWords(
      filters.searchQuery,
      MIN_SEARCH_WORD_LENGTH
    )) {
      q = q.ilike('name', `%${word}%`)
    }
  }

  return q
}

export const model = {
  findOne: (modelId: string) =>
    queryOptions({
      queryKey: ['model', modelId],
      queryFn: async (): Promise<Model | null> => {
        if (!modelId || modelId.trim() === '') {
          return null
        }

        const { data, error } = await supabase
          .from('models')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            model_tags(tags(id, name, color))
          `
          )
          .eq('id', modelId)
          .is('deleted_at', null)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch model: ${error.message}`, {
            cause: error,
          })
        }

        return data as Model | null
      },
    }),

  findOneWithShots: (modelId: string) =>
    queryOptions({
      queryKey: ['model', modelId, 'shots'],
      queryFn: async (): Promise<Talent | null> => {
        if (!modelId || modelId.trim() === '') {
          return null
        }

        const { data, error } = await supabase
          .from('models')
          .select('*, model_shots(*)')
          .eq('id', modelId)
          .is('deleted_at', null)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch talent: ${error.message}`, {
            cause: error,
          })
        }

        return data
      },
    }),

  findManyLibraryByOrg: (
    orgId: string,
    filters: { source: LibrarySource; searchQuery?: string | null }
  ) =>
    infiniteQueryOptions({
      queryKey: ['models', 'library', orgId, filters],
      queryFn: async ({ pageParam = 0 }): Promise<LibraryTalent[]> => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch the Talent Library without an organization ID'
          )
        }

        const from = pageParam * PAGE_SIZE
        const to = from + PAGE_SIZE - 1

        let query = supabase
          .from('models')
          .select('*, model_shots(*), roster_entries(count)')
          .is('workspace_id', null)
          .is('deleted_at', null)
          .is('archived_at', null)
          .order('created_at', { ascending: false })

        if (filters.source === 'org') {
          query = query.eq('org_id', orgId)
        } else if (filters.source === 'lumoo') {
          query = query.is('org_id', null)
        } else {
          query = query.or(`org_id.eq.${orgId},org_id.is.null`)
        }

        const { data, error } = await applyModelFilters(query, {
          searchQuery: filters.searchQuery,
        }).range(from, to)

        if (error) {
          throw new Error(
            `Failed to fetch the Talent Library: ${error.message}`,
            { cause: error }
          )
        }

        return data as LibraryTalent[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === PAGE_SIZE ? allPages.length : undefined,
    }),

  findManyByWorkspace: (
    workspaceId: string,
    filters?: ModelFilters,
    options?: { pageSize?: number }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'models',
        workspaceId,
        filters ?? {},
        options?.pageSize ?? PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0 }): Promise<Model[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch models without valid workspace ID'
          )
        }

        const pageSize = options?.pageSize ?? PAGE_SIZE
        const from = pageParam * pageSize
        const to = from + pageSize - 1

        const baseQuery = supabase
          .from('models')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            model_tags(tags(id, name, color))
          `
          )
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .range(from, to)

        const { data, error } = await applyModelFilters(baseQuery, filters)

        if (error) {
          throw new Error(`Failed to fetch models: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as Model[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const pageSize = options?.pageSize ?? PAGE_SIZE
        return lastPage.length === pageSize ? allPages.length : undefined
      },
    }),

  findManyCompletedByWorkspace: (
    workspaceId: string,
    options?: { searchQuery?: string | null }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'models',
        workspaceId,
        'completed',
        options?.searchQuery ?? null,
        PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0 }): Promise<Model[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch models without valid workspace ID'
          )
        }

        const from = pageParam * PAGE_SIZE
        const to = from + PAGE_SIZE - 1

        let query = supabase
          .from('models')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!inner!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            model_tags(tags(id, name, color))
          `
          )
          .eq('current_generation.status', 'completed')
          .not('current_generation.storage_path', 'is', null)
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })

        if (options?.searchQuery) {
          for (const word of searchWords(
            options.searchQuery,
            MIN_SEARCH_WORD_LENGTH
          )) {
            query = query.ilike('name', `%${word}%`)
          }
        }

        const { data, error } = await query.range(from, to)

        if (error) {
          throw new Error(`Failed to fetch models: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as unknown as Model[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === PAGE_SIZE ? allPages.length : undefined,
    }),

  /**
   * Update model's current generation (called after generation completes)
   */
  updateCurrentGeneration: async (
    modelId: string,
    generationId: string
  ): Promise<void> => {
    const { error } = await supabase
      .from('models')
      .update({ current_generation_id: generationId })
      .eq('id', modelId)

    if (error) {
      throw new Error(
        `Failed to update model current generation: ${error.message}`,
        { cause: error }
      )
    }
  },

  /**
   * Update model metadata (name)
   */
  update: async (modelId: string, input: { name?: string }): Promise<void> => {
    const { error } = await supabase
      .from('models')
      .update({
        ...(input.name !== undefined && { name: input.name }),
      })
      .eq('id', modelId)

    if (error) {
      throw new Error(`Failed to update model: ${error.message}`, {
        cause: error,
      })
    }
  },

  saveToLibrary: async (modelId: string): Promise<void> => {
    const { error } = await supabase.rpc('save_to_talent_library', {
      p_model_id: modelId,
    })

    if (error) {
      throw new Error(
        `Failed to save talent to the Talent Library: ${error.message}`,
        { cause: error }
      )
    }
  },

  archive: async (modelId: string): Promise<void> => {
    const { error } = await supabase
      .from('models')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', modelId)

    if (error) {
      throw new Error(`Failed to archive talent: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Soft delete a model
   */
  delete: async (modelId: string): Promise<void> => {
    if (!modelId || modelId.trim() === '') {
      throw new Error('Invalid modelId: cannot delete model without valid ID')
    }

    const { error } = await supabase
      .from('models')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', modelId)

    if (error) {
      throw new Error(`Failed to delete model: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Soft delete all models in a workspace (with optional filters)
   */
  /**
   * Soft delete multiple models by IDs
   */
  deleteMany: async (ids: string[]): Promise<void> => {
    if (!ids.length) {
      return
    }

    const { error } = await supabase
      .from('models')
      .update({ deleted_at: new Date().toISOString() })
      .in('id', ids)

    if (error) {
      throw new Error(`Failed to delete models: ${error.message}`, {
        cause: error,
      })
    }
  },

  deleteAllByWorkspace: async (
    workspaceId: string,
    filters?: ModelFilters,
    excludeIds?: string[]
  ): Promise<void> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return
    }

    let baseQuery = supabase
      .from('models')
      .update({ deleted_at: new Date().toISOString() })
      .eq('workspace_id', workspaceId)
      .is('deleted_at', null)

    baseQuery = applyModelFilters(baseQuery, filters)

    if (excludeIds && excludeIds.length > 0) {
      baseQuery = baseQuery.not('id', 'in', `(${excludeIds.join(',')})`)
    }

    const { error } = await baseQuery

    if (error) {
      throw new Error(`Failed to delete models: ${error.message}`, {
        cause: error,
      })
    }
  },
}

/**
 * Find a model in any workspace infinite query cache.
 * For use in route loaders (non-hook context).
 */
export function findCachedModel(
  queryClient: QueryClient,
  workspaceId: string,
  modelId: string
): Model | null {
  const queries = queryClient.getQueriesData<InfiniteData<Model[]>>({
    queryKey: ['models', workspaceId],
    exact: false,
  })

  for (const [, data] of queries) {
    const found = data?.pages.flat().find((m) => m.id === modelId)
    if (found) {
      return found
    }
  }
  return null
}
